import { useMemo, useCallback } from 'react';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/hooks/useAuth';
import { useGoalsDB } from '@/hooks/useGoalsDB';
import { supabase } from '@/integrations/supabase/client';
import { reportError } from '@/lib/error-reporting';

/**
 * Determines whether to show the profile enrichment prompt
 * and provides dismiss/snooze actions.
 *
 * Trigger conditions (any one is sufficient):
 * - User has completed at least one goal (archived = true)
 * - Account is 7+ days old
 * - Any habit has a 7+ day streak
 *
 * Suppression:
 * - enrichment_prompt_permanent = true → never show
 * - enrichment_prompt_dismissed_at within last 14 days → snoozed
 */
export function useProfileEnrichment() {
  const { user } = useAuth();
  const { profile, invalidate } = useProfile();
  const { goals, completedGoals, getTotalStreak } = useGoalsDB();

  // Cast for columns not yet in generated types
  const profileAny = profile as Record<string, unknown> | null;
  const permanent = profileAny?.enrichment_prompt_permanent === true;
  const dismissedAt = profileAny?.enrichment_prompt_dismissed_at as string | null | undefined;

  const shouldShow = useMemo(() => {
    if (!profile || !user) return false;
    if (permanent) return false;

    // Check snooze: dismissed within last 14 days
    if (dismissedAt) {
      const dismissed = new Date(dismissedAt);
      const fourteenDaysAgo = new Date();
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
      if (dismissed > fourteenDaysAgo) return false;
    }

    // Trigger 1: completed at least one goal
    if (completedGoals.length > 0) return true;

    // Trigger 2: account is 7+ days old
    const created = new Date(profile.created_at);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    if (created <= sevenDaysAgo) return true;

    // Trigger 3: any habit has a 7+ day streak
    const totalStreak = getTotalStreak();
    if (totalStreak >= 7) return true;

    return false;
  }, [profile, user, permanent, dismissedAt, completedGoals, goals, getTotalStreak]);

  /** Snooze for 14 days */
  const dismiss = useCallback(async () => {
    if (!user) return;
    try {
      await supabase
        .from('profiles')
        .update({ enrichment_prompt_dismissed_at: new Date().toISOString() } as Record<string, unknown>)
        .eq('user_id', user.id);
      invalidate();
    } catch (err) {
      reportError(err, 'settings', { action: 'enrichment-dismiss' });
    }
  }, [user, invalidate]);

  /** Permanently hide */
  const dismissPermanently = useCallback(async () => {
    if (!user) return;
    try {
      await supabase
        .from('profiles')
        .update({
          enrichment_prompt_permanent: true,
          enrichment_prompt_dismissed_at: new Date().toISOString(),
        } as Record<string, unknown>)
        .eq('user_id', user.id);
      invalidate();
    } catch (err) {
      reportError(err, 'settings', { action: 'enrichment-dismiss-permanent' });
    }
  }, [user, invalidate]);

  return { shouldShow, dismiss, dismissPermanently };
}
